import {Swipe, Page} from '@kit';

import React from 'react';
import {gql} from '@apollo/client';
import {useApolloClient} from '@apollo/client/react';

import {MonsterFeatures} from '../features';
import {MonsterAbout} from '../about';
import {PageTools} from '../../../components/page-tools';
import {toMonsterTypeLine} from '../../../components/render-helpers';
import {Monster, useMonsterQuery} from '../../../generated/types';
import {useRoute} from '../../../lib/routes';
import {NotFoundSeo, isNotFoundError, useNotFound} from '../../../lib/seo';
import {MonsterSeo} from '../seo';
import {NotFound} from '../../../components/not-found';

import dashboardCss from '@kit/sidebar/dashboard.module.css';

type Props = {
	monsterId: string;
	title: string;
};

export const MonsterLayout: React.FC<Props> = ({monsterId, title}) => {
	const {routeName, params} = useRoute();

	const client = useApolloClient();
	const monster = client.readFragment<Monster>({
		id: `Monster:${monsterId}`,
		fragment: gql`
			fragment LayoutMonster on Monster {
				id
				title
				size
				types
				subType
				swarmSize
				alignment
			}
		`,
	});

	// тот же запрос, что у вкладок: Apollo отдаёт его из кэша, второй раз в сеть не ходит
	const {data, loading, error} = useMonsterQuery({variables: {id: monsterId}});

	const notFound = !loading && !data?.monster && isNotFoundError(error);
	useNotFound(notFound);

	const monsterVariants = data?.monster && data.monster.legacyMonsters;

	const currentMonsterVariant =
		monsterVariants && routeName === 'monsterVariant'
			? monsterVariants.find((m) => m.id === params.variantId)
			: null;

	const pages: Page[] = [
		{
			route: 'monsterInfo',
			routeParams: {id: monsterId},
			title: 'Описание',
			affectedRoutes: ['monsterInfo'],
			component: MonsterAbout,
			props: {monsterId},
		},
		{
			route: 'monster',
			routeParams: {id: monsterId},
			title: 'Особенности',
			affectedRoutes: ['monster', 'monsterVariant'],
			component: MonsterFeatures,
			props: {
				baseId: monsterId,
				variantId: currentMonsterVariant?.id,
				variants:
					monsterVariants && monsterVariants.length > 0
						? monsterVariants?.map((variant) => ({
								id: variant.id,
								title: variant.title,
								book: variant.source?.at(0)?.book,
						  }))
						: null,
			},
		},
	];

	return (
		<article className={dashboardCss.readingWrapper}>
			{data?.monster && (
				<MonsterSeo
					monster={data.monster as Monster}
					tab={routeName === 'monsterInfo' ? 'about' : 'features'}
				/>
			)}
			{notFound && <NotFoundSeo />}
			<PageTools />
			<h1 className={dashboardCss.mainTitle}>
				{currentMonsterVariant?.title || title}
				<div className="subTitle">
					{(monster || currentMonsterVariant) && (
						<em>{toMonsterTypeLine((currentMonsterVariant || monster) as Monster)}</em>
					)}
				</div>
			</h1>
			{notFound ? <NotFound /> : <Swipe pages={pages} />}
		</article>
	);
};
